/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../theme';
import { authService } from '../services/authService';
import { 
  UserPlus, 
  Mail, 
  Lock, 
  User,
  ArrowRight,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { ROUTES } from '../routes';

const Register: React.FC = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    try {
      await authService.register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate(ROUTES.LOGIN);
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error?.description || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 animate-in fade-in duration-700">
        <div className="w-full max-w-md p-10 rounded-3xl border shadow-2xl text-center space-y-6" 
             style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 size={48} className="text-emerald-500" />
          </div>
          <h2 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>Account Created!</h2>
          <p style={{ color: theme.colors.mutedText }}>
            Welcome to the community. Redirecting you to login...
          </p>
          <div className="pt-4">
            <Link to={ROUTES.LOGIN} className="font-black" style={{ color: theme.colors.primary }}>
              Go to Login now →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[90vh] flex flex-col items-center justify-center p-4 animate-in fade-in duration-700">
      <div className="w-full max-w-lg space-y-8 p-10 rounded-3xl border shadow-2xl relative overflow-hidden" 
           style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        
        {/* Decorative elements */}
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-5 blur-3xl" style={{ backgroundColor: theme.colors.primary }}></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-5 blur-3xl" style={{ backgroundColor: theme.colors.primary }}></div>

        <div className="text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/20" 
               style={{ backgroundColor: theme.colors.primary }}>
            <UserPlus size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>Create Account</h2>
          <p className="mt-2 text-sm font-bold" style={{ color: theme.colors.mutedText }}>
            Join your local marketplace community today
          </p>
        </div>
        
        {error && (
          <div className="p-4 rounded-xl border text-xs font-bold flex items-center gap-2 animate-in shake duration-300" 
               style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-5 relative z-10">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                <User size={14} /> First Name
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>
                Last Name
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
              <Mail size={14} /> Email Address
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="name@example.com"
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
              style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                <Lock size={14} /> Password
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>
                Confirm Password
              </label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
              />
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-2xl font-black text-white transition-all hover:opacity-90 shadow-xl flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: theme.colors.primary }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Create Account <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>
        </form>

        <div className="pt-6 border-t text-center relative z-10" style={{ borderColor: theme.colors.border }}>
          <p className="text-sm font-bold" style={{ color: theme.colors.mutedText }}>
            Already have an account?{' '}
            <Link to={ROUTES.LOGIN} className="font-black hover:opacity-70 transition-opacity" style={{ color: theme.colors.primary }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
